import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ArrowRight } from "lucide-react";
import ImageUpload from "@/components/admin/ImageUpload";
import { fmtCOP } from "@/lib/format";

const empty = { origin: "Bogotá", destination: "", price_from: 0, duration_hours: "", description: "", passengers_base: "", photo_path: null, visible: true, order: 0 };

export default function RoutesAdmin() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/routes")
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRows([]));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        origin: editing.origin.trim(),
        destination: editing.destination.trim(),
        price_from: Number(editing.price_from) || 0,
        duration_hours: editing.duration_hours ? Number(editing.duration_hours) : null,
        description: editing.description || null,
        passengers_base: editing.passengers_base ? Number(editing.passengers_base) : null,
        photo_path: editing.photo_path || null,
        visible: !!editing.visible,
        order: Number(editing.order) || 0,
      };
      if (editing.id) {
        const { data } = await api.patch(`/admin/routes/${editing.id}`, body);
        setRows((rs) => (Array.isArray(rs) ? rs : []).map((r) => r.id === data.id ? data : r));
      } else {
        const { data } = await api.post("/admin/routes", body);
        setRows((rs) => [...(Array.isArray(rs) ? rs : []), data]);
      }
      toast.success("Guardado");
      setEditing(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar ruta?")) return;
    await api.delete(`/admin/routes/${id}`);
    setRows((rs) => (Array.isArray(rs) ? rs : []).filter((r) => r.id !== id));
    toast.success("Ruta eliminada");
  };

  const safeRows = Array.isArray(rows) ? rows : [];

  return (
    <div data-testid="routes-admin">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[#E8C87A] text-xs uppercase tracking-[0.35em] mb-2">Rutas</div>
          <h1 className="font-display text-3xl md:text-4xl">Rutas populares</h1>
        </div>
        <button data-testid="r-new" onClick={() => setEditing({ ...empty })} className="btn-gold px-4 py-2.5 rounded-full text-sm inline-flex items-center gap-2"><Plus size={14} /> Nueva ruta</button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {safeRows.map((r) => (
          <div key={r.id} data-testid={`r-item-${r.id}`} className="card-lux p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#F5EFE0]/55 mb-3">
              {r.origin} <ArrowRight size={12} className="text-[#E8C87A]" /> <span className="text-[#E8C87A]">{r.destination}</span>
            </div>
            <div className="font-display text-xl text-[#F5EFE0]">Desde {fmtCOP(r.price_from)}</div>
            <div className="text-xs text-[#F5EFE0]/50 mt-1">{r.duration_hours ? `${r.duration_hours} h aprox.` : ""}</div>
            {r.description && <p className="text-sm text-[#F5EFE0]/60 mt-3 line-clamp-2">{r.description}</p>}
            <div className="flex justify-between items-center mt-4 pt-3 border-t border-[#3A3229]">
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${r.visible ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-[#3A3229] text-[#F5EFE0]/50"}`}>
                {r.visible ? "Visible" : "Oculto"}
              </span>
              <div className="flex gap-2">
                <button onClick={() => setEditing({ ...r })} data-testid={`r-edit-${r.id}`} className="p-1.5 rounded-md border border-[#3A3229] text-[#E8C87A] hover:border-[#D4AF37]"><Pencil size={13} /></button>
                <button onClick={() => del(r.id)} data-testid={`r-del-${r.id}`} className="p-1.5 rounded-md border border-[#3A3229] text-[#F5EFE0]/60 hover:border-rose-500/60 hover:text-rose-400"><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
        {safeRows.length === 0 && <div className="card-lux p-10 col-span-full text-center text-[#F5EFE0]/50">Añade tu primera ruta.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/70 px-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="card-lux w-full max-w-lg p-6 md:p-8 bg-[#171412] max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl">{editing.id ? "Editar ruta" : "Nueva ruta"}</h2>
              <button onClick={() => setEditing(null)} className="p-1 text-[#F5EFE0]/60"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <ImageUpload path={editing.photo_path} onChange={(p) => setEditing((f) => ({ ...f, photo_path: p }))} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#F5EFE0]/60 block mb-1">Origen</label>
                  <input data-testid="r-origin" value={editing.origin} onChange={(e) => setEditing({ ...editing, origin: e.target.value })} className="w-full bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2.5 outline-none focus:border-[#D4AF37]" />
                </div>
                <div>
                  <label className="text-xs text-[#F5EFE0]/60 block mb-1">Destino</label>
                  <input data-testid="r-destination" value={editing.destination} onChange={(e) => setEditing({ ...editing, destination: e.target.value })} className="w-full bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2.5 outline-none focus:border-[#D4AF37]" />
                </div>
                <div>
                  <label className="text-xs text-[#F5EFE0]/60 block mb-1">Precio desde (COP)</label>
                  <input data-testid="r-price" type="number" min="0" value={editing.price_from} onChange={(e) => setEditing({ ...editing, price_from: e.target.value })} className="w-full bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2.5 outline-none focus:border-[#D4AF37]" />
                </div>
                <div>
                  <label className="text-xs text-[#F5EFE0]/60 block mb-1">Duración (horas)</label>
                  <input type="number" step="0.5" value={editing.duration_hours} onChange={(e) => setEditing({ ...editing, duration_hours: e.target.value })} className="w-full bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2.5 outline-none focus:border-[#D4AF37]" />
                </div>
                <div>
                  <label className="text-xs text-[#F5EFE0]/60 block mb-1">Personas base</label>
                  <input type="number" min="1" value={editing.passengers_base || ""} onChange={(e) => setEditing({ ...editing, passengers_base: e.target.value })} placeholder="5" className="w-full bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2.5 outline-none focus:border-[#D4AF37]" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#F5EFE0]/60 block mb-1">Descripción</label>
                <textarea value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2.5 min-h-[80px] resize-none outline-none focus:border-[#D4AF37]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editing.visible} onChange={(e) => setEditing({ ...editing, visible: e.target.checked })} className="accent-[#D4AF37]" />
                  Visible en el sitio
                </label>
                <div>
                  <label className="text-xs text-[#F5EFE0]/60 block mb-1">Orden</label>
                  <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className="w-full bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2.5 outline-none focus:border-[#D4AF37]" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditing(null)} className="btn-ghost-gold px-4 py-2 rounded-full text-sm">Cancelar</button>
              <button data-testid="r-save" onClick={save} disabled={saving} className="btn-gold px-4 py-2 rounded-full text-sm">{saving ? "Guardando…" : "Guardar"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
